import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Clock, AlertTriangle, CheckCircle, XCircle, HelpCircle, WifiOff, Battery, Timer, ImageOff, Loader2, Camera } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { aedApi } from '../utils/api';
import { STATUS_CONFIG, formatConfidence, formatTimestamp } from '../utils/constants';
import { Badge } from './ui/badge';

const StatusIcon = ({ status }) => {
  const icons = {
    READY: CheckCircle,
    NOT_READY: AlertTriangle,
    REPOSITION: AlertTriangle,
    AED_NOT_PRESENT: XCircle,
    BATTERY_EXPIRED: Battery,
    EXPIRING_BATT_PADS: Timer,
    CAMERA_ISSUE: Camera,
    UNKNOWN: HelpCircle,
    LOST_CONTACT: WifiOff
  };
  const Icon = icons[status] || HelpCircle;
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.UNKNOWN;
  return <Icon className={`w-4 h-4 ${config.color}`} />;
};

const CardImage = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  if (!src || error) {
    return (
      <div
        className="w-16 h-16 flex-shrink-0 rounded-lg bg-slate-800/80 border border-slate-700 flex items-center justify-center"
        title="No device image"
      >
        <ImageOff className="w-6 h-6 text-slate-500" />
      </div>
    );
  }
  return (
    <div className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border border-slate-700 bg-slate-800">
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
          <div className="w-5 h-5 border-2 border-cyan-500/50 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

const AEDCard = ({ aed, index, onClick }) => {
  const config = STATUS_CONFIG[aed.current_status] || STATUS_CONFIG.UNKNOWN;
  
  return (
    <div
      data-testid={`aed-card-${aed.id}`}
      onClick={() => onClick(aed)}
      className={`
        relative group p-4 rounded-xl border bg-slate-900/20 backdrop-blur-sm 
        cursor-pointer overflow-hidden transition-all duration-300
        hover:border-cyan-500/50 hover:bg-slate-800/40
        card-lift animate-fade-in-up
        ${aed.review_required ? 'border-amber-500/50 pulse-review' : 'border-slate-800'}
      `}
      style={{ animationDelay: `${index * 30}ms` }}
    >
      {/* Glow effect on hover - pointer-events-none ensures it doesn't block clicks */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-transparent pointer-events-none" />
      </div>
      
      <div className="relative flex items-start gap-3 z-10">
        <CardImage src={aed.last_image_url} alt={`AED ${aed.aed_id}`} />
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-mono text-sm font-semibold text-white truncate">
              {aed.aed_id}
            </h3>
            {aed.review_required && (
              <Badge 
                variant="outline" 
                className="text-[9px] bg-amber-500/20 border-amber-500/50 text-amber-400 px-1.5"
              >
                REVIEW
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-1 text-xs text-slate-500 mb-3">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{aed.location}</span>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge 
              variant="outline" 
              className={`${config.bgColor} ${config.color} text-[10px] font-mono uppercase`}
            >
              <StatusIcon status={aed.current_status} />
              <span className="ml-1">{config.label}</span>
            </Badge>
            
            <div className="flex items-center gap-1">
              <div 
                className="h-1.5 w-16 bg-slate-800 rounded-full overflow-hidden"
                title={`AI Confidence: ${formatConfidence(aed.ai_confidence)}`}
              >
                <div 
                  className={`h-full rounded-full transition-all ${
                    aed.ai_confidence >= 0.85 ? 'bg-emerald-500' :
                    aed.ai_confidence >= 0.70 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${aed.ai_confidence * 100}%` }}
                />
              </div>
              <span className="text-[10px] font-mono text-slate-500">
                {formatConfidence(aed.ai_confidence)}
              </span>
            </div>
          </div>
        </div>
        
        <div className="text-right flex flex-col items-end gap-1">
          <span className="text-[10px] font-mono text-slate-600 uppercase">
            {aed.subscriber}
          </span>
          <div className="flex items-center gap-1 text-[10px] text-slate-500">
            <Clock className="w-3 h-3" />
            <span>{formatTimestamp(aed.updated_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PAGE_SIZE = 30;

export const AEDList = () => {
  const { filters, openDetail, refreshKey } = useApp();
  const [aeds, setAeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = await aedApi.getAll(filters, { limit: PAGE_SIZE, skip: aeds.length });
      setAeds((prev) => [...prev, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to load more AEDs:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [filters, aeds.length, hasMore, loadingMore]);

  useEffect(() => {
    setAeds([]);
    setHasMore(true);
    setLoading(true);
    setError(null);
    const fetchFirst = async () => {
      try {
        const data = await aedApi.getAll(filters, { limit: PAGE_SIZE, skip: 0 });
        setAeds(data);
        setHasMore(data.length === PAGE_SIZE);
      } catch (err) {
        console.error('Failed to fetch AEDs:', err);
        setError('Failed to load AEDs. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchFirst();
  }, [filters, refreshKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 200;
      if (nearBottom && hasMore && !loadingMore && !loading) loadMore();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, loadingMore, loading, loadMore]);

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold tracking-wide text-white">
            AED MONITORING GRID
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-28 bg-slate-900/40 rounded-xl border border-slate-800 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-semibold tracking-wide text-white">
          AED MONITORING GRID
        </h2>
        <span className="text-xs font-mono text-slate-500">
          {aeds.length} units displayed
        </span>
      </div>
      
      {aeds.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <HelpCircle className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 mb-2">No AEDs found</p>
            <p className="text-sm text-slate-600">Try adjusting your filters</p>
          </div>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto pr-2 aed-grid-scrollbar"
          data-testid="aed-list-scroll"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 pb-4" data-testid="aed-list">
            {[...aeds]
              .sort((a, b) => {
                const aNeedsReview = a.review_required === true;
                const bNeedsReview = b.review_required === true;
                if (aNeedsReview && !bNeedsReview) return -1;
                if (!aNeedsReview && bNeedsReview) return 1;
                const aReady = a.current_status === 'READY';
                const bReady = b.current_status === 'READY';
                if (!aReady && bReady) return -1;
                if (aReady && !bReady) return 1;
                return 0;
              })
              .map((aed, index) => (
                <AEDCard
                  key={aed.id}
                  aed={aed}
                  index={index}
                  onClick={openDetail}
                />
              ))}
          </div>
          {loadingMore && (
            <div className="flex items-center justify-center gap-2 py-6 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading more devices…</span>
            </div>
          )}
          {!hasMore && aeds.length > 0 && !loadingMore && (
            <div className="text-center py-4 text-xs text-slate-500">
              All devices loaded
            </div>
          )}
        </div>
      )}
    </div>
  );
};
